import { createInertiaApp, router } from "@inertiajs/react";
import { PostHogApp, isCloudPath, isNightwatchPath, syncPostHogCapture, capturePageView } from "./posthog";
import { hydrateRoot, createRoot } from "react-dom/client";

// Toggle the forge-themed Inertia progress bar based on destination URL.
// Paired with #nprogress overrides in app.css.
function isForgePath(pathname) {
    return pathname === "/forge" || pathname.startsWith("/forge/");
}

function isFoundersPath(pathname) {
    return pathname === "/founders" || pathname.startsWith("/founders/");
}

function syncPageTheme(pathname) {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("inertia-forge", isForgePath(pathname));
    document.documentElement.classList.toggle("inertia-cloud", isCloudPath(pathname));
    document.documentElement.classList.toggle("inertia-nightwatch", isNightwatchPath(pathname));
    document.documentElement.classList.toggle("inertia-founders", isFoundersPath(pathname));
}

// Cloud pages need a different dark mode <body> background color.
function syncPageBackground(pathname) {
    if (typeof document === "undefined") return;
    const isCloud = isCloudPath(pathname);
    document.body.classList.toggle("dark:bg-[#00001e]", isCloud);
    document.body.classList.toggle("dark:bg-neutral-900", !isCloud);
}

// Alternative to `onLocationChange` that uses the `navigate` event to also work with cached page changes
function onNavigate(callback) {
    if (typeof window === "undefined") return;
    callback(window.location.pathname);
    router.on("navigate", (event) => {
        try {
            callback(new URL(event.detail.page.url, window.location.origin).pathname);
        } catch {
            // ignore — fall back to whatever class is already set
        }
    });
}

onNavigate((pathname) => {
    syncPageTheme(pathname);
    syncPageBackground(pathname);
    syncPostHogCapture(pathname);
    capturePageView(pathname);
});

createInertiaApp({
    progress: {
        color: "#F53003",
    },
    resolve: (name) => {
        const pages = import.meta.glob("./pages/**/page.tsx");
        return pages[`./pages/${name}/page.tsx`]();
    },
    setup({ el, App, props }) {
        const app = (
            <PostHogApp>
                <App {...props} />
            </PostHogApp>
        );

        if (el.hasChildNodes()) {
            hydrateRoot(el, app);
        } else {
            createRoot(el).render(app);
        }
    },
});
